import { Box, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';

import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import TypeIcon from '@/features/portfolio-analysis/components/datagrid/TypeIcon';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../utils/formatters';
import EnergyMark from '../components/datagrid/EnergyMark';
import {
  CustomHeaderWithLabel,
  displayTime,
  isGroupRow,
  isLeafRow,
} from '../utils/datagrid-helpers';
import OccupancyDaysCell from '@/features/portfolio-analysis/components/datagrid/OccupancyDaysCell';
import DataIntegrityButton from '@/features/portfolio-analysis/components/data-integrity/DataIntegrityButton';
export const useDashboardColumns = (transformedData, preset) => {
  const { t } = useTranslation('portfolio-analysis_dashboard');

  const firstColumns = useMemo(() => {
    let result = preset.columnGroupingModel.map((group) => {
      return group.children[0]?.field;
    });
    result.push('warnings');
    return result;
  }, [preset]);

  const columns = useMemo(() => {
    const cols = [
      {
        flex: 0,
        aggregable: false,
        disableColumnMenu: true,
        hideSortIcons: true,
        field: 'favorites',
        type: 'boolean',
        minWidth: 50,
        width: 50,
        renderCell: (params) => {
          if (
            params.rowNode.isAutoGenerated &&
            params.rowNode?.children?.length > 0
          ) {
            const allChildrenFavorited = params.rowNode.children.every(
              (childId) => {
                const child = params.api.getRow(childId);
                return child.favorites;
              },
            );

            return allChildrenFavorited ? (
              <StarRoundedIcon
                sx={{ minWidth: 50, cursor: 'pointer' }}
                color="warning"
              />
            ) : (
              <StarBorderRoundedIcon
                sx={{ minWidth: 50, cursor: 'pointer' }}
                color="primary"
              />
            );
          }

          if (params.rowNode.isAutoGenerated) return null;

          return !params.value ? (
            <StarBorderRoundedIcon
              sx={{ minWidth: 50, cursor: 'pointer' }}
              color="primary"
            />
          ) : (
            <StarRoundedIcon
              sx={{ minWidth: 50, cursor: 'pointer' }}
              color="warning"
            />
          );
        },
      },
      {
        field: 'matrikel_key',
        type: 'string',
        aggregable: false,
        groupingValueGetter: (params) => {
          let name = '';
          if (params == null) return null;
          let groupList = [
            ...transformedData.portfolio_analysis_list.filter((item) => {
              if (item.matrikel_key === params) {
                name = item.main_address;
              }
              return item.matrikel_key === params;
            }),
          ];
          return groupList.length > 1 ? name : name;
        },
        hideable: false,
      },
      {
        field: 'type',
        headerName: 'SEU',
        minWidth: 50,
        width: 50,
        flex: 0,
        renderCell: (params) => <TypeIcon params={params} />,
      },
      {
        field: 'main_address',
        type: 'string',
        aggregable: false,
        hideable: false,
      },
      {
        field: 'address',
        minWidth: 250,
        type: 'string',
        hideable: false,
        aggregable: false,
      },
      {
        field: 'power_total_consumption_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'heat_total_consumption_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'water_total_consumption_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'occupancy',
        minWidth: 140,
        maxWidth: 140,
        type: 'string',
        aggregable: false,
        renderCell: (params) => {
          return <OccupancyDaysCell daysOfWeek={params.value} />;
        },
      },
      {
        field: 'daytime_start',
        minWidth: 80,
        type: 'number',
        valueFormatter: (value) => displayTime(value),
        renderCell: (params) => {
          return (
            <Box
              sx={{
                fontWeight:
                  params?.row?.daytime_period_source === 'manual' ? 600 : 400,
              }}
            >
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'daytime_end',
        minWidth: 80,
        type: 'number',
        valueFormatter: (value) => displayTime(value),
        renderCell: (params) => {
          return (
            <Box
              sx={{
                fontWeight:
                  params?.row?.daytime_period_source === 'manual' ? 600 : 400,
              }}
            >
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'kWhPrYPrM2',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params);
        },
        minWidth: 180,
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.VerificationFlag ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'AverageBuildingYear',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.AverageBuildingYear ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
        valueFormatter: (value) => {
          return value === -Infinity ? '-' : value?.toFixed(0);
        },
      },
      {
        field: 'SumBuildingArea',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.SumBuildingArea ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'ExtractedBuildingUse',
        type: 'string',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            if (row?.BuildingUsesWithArea == undefined) return null;

            let jsonObject = JSON.parse(
              row?.BuildingUsesWithArea?.replaceAll("'", '"'),
            );
            let result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            if (result.length === 1)
              return (
                <Box
                  sx={{
                    fontWeight: row?.overwrite?.ExtractedBuildingUse
                      ? 600
                      : 400,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {params.formattedValue}
                </Box>
              );
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: row?.overwrite?.ExtractedBuildingUse
                        ? 600
                        : 400,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {params.formattedValue}
                  </Box>
                </div>
                <InfoTooltipIcon
                  title={
                    <>
                      {result.map((buildingType, index) => (
                        <div key={index}>
                          <Typography variant="body2">
                            {buildingType.key}: {buildingType.value} m²
                          </Typography>
                        </div>
                      ))}
                    </>
                  }
                />
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'ExtractedHeatInstallation',
        type: 'string',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }

            if (row?.HeatInstallationsWithArea == undefined) return null;

            let jsonObject = JSON.parse(
              row?.HeatInstallationsWithArea?.replaceAll("'", '"'),
            );
            let result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            if (result.length === 1)
              return (
                <Box
                  sx={{
                    fontWeight: row?.overwrite?.ExtractedHeatInstallation
                      ? 600
                      : 400,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {params.formattedValue}
                </Box>
              );
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: row?.overwrite?.ExtractedHeatInstallation
                        ? 600
                        : 400,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {params.formattedValue}
                  </Box>
                </div>
                <InfoTooltipIcon
                  title={
                    <>
                      {result.map((buildingType, index) => (
                        <div key={index}>
                          <Typography variant="body2">
                            {buildingType.key}: {buildingType.value} m²
                          </Typography>
                        </div>
                      ))}
                    </>
                  }
                />
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'EnergyLabelClassification',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }

            if (
              row.EnergyLabelCountOverview == null ||
              row.EnergyLabelClassification == null
            )
              return null;

            let jsonObject = JSON.parse(
              row?.EnergyLabelCountOverview?.replaceAll("'", '"'),
            );
            let result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <EnergyMark mark={row?.EnergyLabelClassification} />
                {result.length > 1 && (
                  <InfoTooltipIcon
                    title={
                      <>
                        <Typography variant="body1">
                          {t('tooltips.energymarksFound')}
                        </Typography>
                        {result.map((buildingType, index) => (
                          <div key={index}>
                            <Typography variant="body2">
                              {buildingType.key}: {buildingType.value}
                            </Typography>
                          </div>
                        ))}
                      </>
                    }
                  />
                )}
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
        type: 'string',
      },
      {
        field: 'warnings',
        type: 'actions',
        aggregable: false,
        hideable: false,
        groupable: false,
        minWidth: 60,
        maxWidth: 60,
        renderCell: (params) => {
          if (isGroupRow(params)) {
            const children = params.rowNode.children?.map((childId) => {
              const child = params.api.getRow(childId);
              return child;
            });
            if (children.some((r) => !r.meter_in_sync)) {
              return (
                <Tooltip title={t('warnings.rowOutdated')} placement="top">
                  <SyncRoundedIcon
                    color="info"
                    sx={{
                      cursor: 'pointer',
                      marginLeft: 1,
                      animation: 'spin 2s linear infinite',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(360deg)',
                        },
                        '100%': {
                          transform: 'rotate(0deg)',
                        },
                      },
                    }}
                  />
                </Tooltip>
              );
            } else {
              return null;
            }
          }

          if (!params.row.meter_in_sync) {
            return (
              <Tooltip title={t('warnings.rowOutdated')} placement="top">
                <SyncRoundedIcon
                  color="info"
                  sx={{
                    cursor: 'pointer',
                    marginLeft: 1,
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(360deg)',
                      },
                      '100%': {
                        transform: 'rotate(0deg)',
                      },
                    },
                  }}
                />
              </Tooltip>
            );
          }
          return <DataIntegrityButton row={params.row} />;
        },
      },
    ];

    return cols.map((column) => {
      return {
        flex: 1,
        type: 'number',
        minWidth: 140,
        //groupable: false,
        ...column,
        renderHeader: (params) => (
          <CustomHeaderWithLabel props={params} t={t} />
        ),
        headerName: t(`columns.${column.field}.header`),
        cellClassName: firstColumns.includes(column.field)
          ? 'cell-left-border'
          : '',
      };
    });
  }, [firstColumns, t, transformedData.portfolio_analysis_list]);
  return columns;
};
